import type { Player, Team } from '@core/types';
import type { ScoreState } from '@core/utils/scoreboard';
import { indexedDbBackend } from './db';
import type { Backend } from './db';

// Operações de domínio sobre o armazenamento local. O backend é injetado —
// mesmo padrão do `rng` no core — para que os testes rodem contra memória sem
// precisar de IndexedDB, que não existe no ambiente Node do vitest.

export interface DrawSettings {
  numTeams: number;
  playersPerTeam: number;
  balanceByGender: boolean;
}

export type DrawMode = 'names' | 'balanced';

export interface StoredDraw {
  teams: Team[];
  mode: DrawMode;
  balanceByGender: boolean;
  createdAt: string; // ISO 8601
}

export const DEFAULT_SETTINGS: DrawSettings = {
  numTeams: 2,
  playersPerTeam: 5,
  balanceByGender: false,
};

const KEYS = {
  names: 'names',
  players: 'players',
  selectedIds: 'selectedIds',
  settings: 'settings',
  lastDraw: 'lastDraw',
  score: 'score',
} as const;

const defaultBackend = indexedDbBackend();

// Lista de nomes do modo grátis. Só texto — sem nota e sem gênero, que são do
// nível pago (ver §7 do ARQUITETURA-WEB.md).
export async function getNames(backend: Backend = defaultBackend): Promise<string[]> {
  return (await backend.read<string[]>(KEYS.names)) ?? [];
}

export async function saveNames(names: string[], backend: Backend = defaultBackend): Promise<void> {
  await backend.write(KEYS.names, names);
}

export async function getPlayers(backend: Backend = defaultBackend): Promise<Player[]> {
  return (await backend.read<Player[]>(KEYS.players)) ?? [];
}

export async function savePlayers(
  players: Player[],
  backend: Backend = defaultBackend,
): Promise<void> {
  await backend.write(KEYS.players, players);
}

// Quem está presente hoje. Persistido, e não só em estado de tela, para que ir
// da lista para a configuração e recarregar não perca a seleção.
//
// Filtra contra a lista atual na leitura: um jogador excluído do elenco não
// pode continuar "selecionado" e reaparecer no sorteio.
export async function getSelectedIds(
  players: Player[],
  backend: Backend = defaultBackend,
): Promise<string[]> {
  const stored = (await backend.read<string[]>(KEYS.selectedIds)) ?? [];
  const known = new Set(players.map((p) => p.id));
  return stored.filter((id) => known.has(id));
}

export async function saveSelectedIds(
  ids: string[],
  backend: Backend = defaultBackend,
): Promise<void> {
  await backend.write(KEYS.selectedIds, ids);
}

export async function getSettings(backend: Backend = defaultBackend): Promise<DrawSettings> {
  const stored = await backend.read<Partial<DrawSettings>>(KEYS.settings);
  // Mescla com o padrão em vez de devolver o que está gravado: uma versão
  // futura que acrescente campo não quebra para quem já tem dados salvos.
  return { ...DEFAULT_SETTINGS, ...stored };
}

export async function saveSettings(
  settings: DrawSettings,
  backend: Backend = defaultBackend,
): Promise<void> {
  await backend.write(KEYS.settings, settings);
}

// Guarda **um** sorteio, não histórico. Histórico é do nível pago e vem na
// Fatia 6; aqui o objetivo é só não perder os times ao fechar a aba.
export async function getLastDraw(backend: Backend = defaultBackend): Promise<StoredDraw | null> {
  return (await backend.read<StoredDraw>(KEYS.lastDraw)) ?? null;
}

export async function saveLastDraw(
  draw: StoredDraw,
  backend: Backend = defaultBackend,
): Promise<void> {
  await backend.write(KEYS.lastDraw, draw);
}

export async function clearLastDraw(backend: Backend = defaultBackend): Promise<void> {
  await backend.remove(KEYS.lastDraw);
}

interface StoredScore {
  state: ScoreState;
  updatedAt: number;
}

/**
 * Por quanto tempo um placar interrompido ainda é "a partida de agora".
 *
 * O placar é gravado para sobreviver ao navegador descartar a aba no meio do
 * jogo — não para virar histórico. Doze horas cobrem qualquer intervalo real
 * (chuva, pneu furado, prorrogação) e ainda garantem que o jogo de terça não
 * apareça na quinta como se estivesse em andamento.
 */
export const SCORE_MAX_AGE_MS = 12 * 60 * 60 * 1000;

export async function getScore(
  now: number = Date.now(),
  backend: Backend = defaultBackend,
): Promise<ScoreState | null> {
  const stored = await backend.read<StoredScore>(KEYS.score);
  if (!stored) return null;

  // Relógio para trás (fuso, ajuste do sistema) também descarta: `now - updatedAt`
  // negativo não é um placar recente, é um dado em que não dá para confiar.
  const age = now - stored.updatedAt;
  if (age < 0 || age > SCORE_MAX_AGE_MS) return null;

  return stored.state;
}

export async function saveScore(
  state: ScoreState,
  now: number = Date.now(),
  backend: Backend = defaultBackend,
): Promise<void> {
  await backend.write<StoredScore>(KEYS.score, { state, updatedAt: now });
}

export async function clearScore(backend: Backend = defaultBackend): Promise<void> {
  await backend.remove(KEYS.score);
}
